import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSignalR } from "src/hooks";
import { configuration } from "../../../configuration";
import { ActionItemDetailDto, ActionItemSummaryDto, FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  actionItemGuid: string;
  planGuid: string;
};

const useDeleteActionItemRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteActionItemRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "delete",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteActionItemRequest };
};

const useDeleteActionItem = () => {
  const { deleteActionItemRequest } = useDeleteActionItemRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: deleteActionItemRequest,
    onMutate: async (variables) => {
      await queryClient.cancelQueries(queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid));
      await queryClient.cancelQueries(queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid));

      const cachedDetailData = queryClient.getQueryData<ActionItemDetailDto>(
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid)
      );
      queryClient.removeQueries(queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid));
      const cachedSummaryData = queryClient.getQueriesData<ActionItemSummaryDto>(
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid)
      );

      return { cachedSummaryData, cachedDetailData };
    },
    onError: (_error, variables, context) => {
      queryClient.setQueriesData(
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        context?.cachedSummaryData
      );

      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        context?.cachedDetailData
      );
    },
    onSuccess: (_data, variables, _context) => {
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useDeleteActionItem };
